'use client';

import { Box, Checkbox, ListItemText, Menu, MenuItem } from '@mui/material';
import { Toolbar, ColumnsPanelTrigger, ExportCsv, useGridApiContext, GridDensity } from '@mui/x-data-grid-pro';
import { gridColumnVisibilityModelSelector, useGridSelector, type GridColumnVisibilityModel } from '@mui/x-data-grid';
import type * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DensityMediumIcon from '@mui/icons-material/DensityMedium';
import LayersIcon from '@mui/icons-material/Layers';
import {
  PROJECT_GRID_VISIBILITY_GROUPS,
  enforceProjectGridNonHideableColumns,
} from '@/lib/constants/projectGridColumnGroups';

export type ProjectGridToolbarProps = {
  /** Aligns with ProjectApiController search: which sample columns are populated. */
  canViewSampleRequestCounts: boolean;
  canViewShippingCounts: boolean;
  canViewSamplesDueInfo: boolean;
};

function isFieldVisibleInModel(model: GridColumnVisibilityModel, field: string): boolean {
  return model[field] !== false;
}

/** Fields in a group that the user is allowed to toggle (Samples respects privileges). */
function getGroupFieldsForToggle(
  groupId: string,
  fields: readonly string[],
  p: Pick<ProjectGridToolbarProps, 'canViewSampleRequestCounts' | 'canViewShippingCounts' | 'canViewSamplesDueInfo'>
): string[] {
  if (groupId !== 'samples') {
    return [...fields];
  }
  return fields.filter((f) => {
    if (f === 'totalNbSamplesRequested') return p.canViewSampleRequestCounts;
    if (f === 'nbSamplesShipped') return p.canViewShippingCounts;
    if (f === 'totalNbSamples' || f === 'nbSamplesDue') return p.canViewSamplesDueInfo;
    return true;
  });
}

function getGroupCheckboxState(
  model: GridColumnVisibilityModel,
  fields: string[]
): { allOn: boolean; indeterminate: boolean } {
  if (fields.length === 0) {
    return { allOn: false, indeterminate: false };
  }
  let on = 0;
  for (const f of fields) {
    if (isFieldVisibleInModel(model, f)) on++;
  }
  if (on === 0) return { allOn: false, indeterminate: false };
  if (on === fields.length) return { allOn: true, indeterminate: false };
  return { allOn: false, indeterminate: true };
}

/**
 * Project search grid toolbar: Columns, Groups (bulk show/hide), Density, Export.
 */
export function ProjectGridToolbar({
  canViewSampleRequestCounts,
  canViewShippingCounts,
  canViewSamplesDueInfo,
}: ProjectGridToolbarProps) {
  const apiRef = useGridApiContext();
  const visibilityModel = useGridSelector(apiRef, gridColumnVisibilityModelSelector);

  const [groupsAnchor, setGroupsAnchor] = useState<null | HTMLElement>(null);
  const [densityAnchor, setDensityAnchor] = useState<null | HTMLElement>(null);

  const privilegeProps = useMemo(
    () => ({ canViewSampleRequestCounts, canViewShippingCounts, canViewSamplesDueInfo }),
    [canViewSampleRequestCounts, canViewShippingCounts, canViewSamplesDueInfo]
  );

  const applyGroupVisibility = useCallback(
    (fields: string[], visible: boolean) => {
      const current = gridColumnVisibilityModelSelector(apiRef) ?? {};
      const next: GridColumnVisibilityModel = { ...current };
      for (const f of fields) {
        next[f] = visible;
      }
      apiRef.current.setColumnVisibilityModel(enforceProjectGridNonHideableColumns(next));
    },
    [apiRef]
  );

  const handleGroupsClose = () => setGroupsAnchor(null);

  const handleDensityClick = (event: React.MouseEvent<HTMLElement>) => {
    setDensityAnchor(event.currentTarget);
  };

  const handleDensityClose = () => {
    setDensityAnchor(null);
  };

  const handleDensitySelect = (density: GridDensity) => {
    apiRef.current.setDensity(density);
    handleDensityClose();
  };

  return (
    <Toolbar>
      <ColumnsPanelTrigger startIcon={<ViewColumnIcon />}>Columns</ColumnsPanelTrigger>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <LayersIcon sx={{ color: 'primary.main', fontSize: 18 }} />
        <Box
          component="button"
          type="button"
          onClick={(e) => setGroupsAnchor(e.currentTarget)}
          sx={{
            border: 'none',
            background: 'none',
            color: 'primary.main',
            fontSize: '0.875rem',
            fontFamily: 'inherit',
            cursor: 'pointer',
            padding: '6px 8px',
            display: 'flex',
            alignItems: 'center',
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.04)',
              borderRadius: 1,
            },
          }}
        >
          Groups
        </Box>
      </Box>

      <Menu anchorEl={groupsAnchor} open={Boolean(groupsAnchor)} onClose={handleGroupsClose}>
        {PROJECT_GRID_VISIBILITY_GROUPS.map((group) => {
          const fields = getGroupFieldsForToggle(group.id, group.fields, privilegeProps);
          const { allOn, indeterminate } = getGroupCheckboxState(visibilityModel ?? {}, fields);
          const disabled = group.id === 'samples' && fields.length === 0;

          return (
            <MenuItem
              key={group.id}
              dense
              disabled={disabled}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (fields.length === 0) return;
                applyGroupVisibility(fields, !allOn);
              }}
            >
              <Checkbox
                checked={allOn}
                indeterminate={indeterminate}
                size="small"
                sx={{ mr: 1, p: 0 }}
                tabIndex={-1}
                disableRipple
              />
              <ListItemText primary={group.label} />
            </MenuItem>
          );
        })}
      </Menu>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <DensityMediumIcon sx={{ color: 'primary.main', fontSize: 18 }} />
        <Box
          component="button"
          type="button"
          onClick={handleDensityClick}
          sx={{
            border: 'none',
            background: 'none',
            color: 'primary.main',
            fontSize: '0.875rem',
            fontFamily: 'inherit',
            cursor: 'pointer',
            padding: '6px 8px',
            display: 'flex',
            alignItems: 'center',
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.04)',
              borderRadius: 1,
            },
          }}
        >
          Density
        </Box>
      </Box>

      <Menu anchorEl={densityAnchor} open={Boolean(densityAnchor)} onClose={handleDensityClose}>
        <MenuItem onClick={() => handleDensitySelect('compact')}>Compact</MenuItem>
        <MenuItem onClick={() => handleDensitySelect('standard')}>Standard</MenuItem>
        <MenuItem onClick={() => handleDensitySelect('comfortable')}>Comfortable</MenuItem>
      </Menu>

      <ExportCsv
        startIcon={<FileDownloadIcon />}
        options={{
          fileName: 'projects',
          delimiter: ',',
          utf8WithBom: true,
        }}
      >
        Export
      </ExportCsv>
    </Toolbar>
  );
}
